/* eslint-disable prettier/prettier */
import { useCallback, useState } from 'react';
import {
	reviewAssessmentSubmissionService,
	ReviewAssessmentSubmissionPayload,
	AssessmentSubmission,
} from '../services/reviewAssessmentSubmission.service';

interface UseReviewAssessmentSubmissionReturn {
	reviewSubmission: (
		submissionId: string,
		payload: ReviewAssessmentSubmissionPayload
	) => Promise<AssessmentSubmission | null>;

	data: AssessmentSubmission | null;
	loading: boolean;
	error: string | null;
	success: boolean;
	message: string | null;
	reset: () => void;
}

export const useReviewAssessmentSubmission =
	(): UseReviewAssessmentSubmissionReturn => {
		const [data, setData] = useState<AssessmentSubmission | null>(null);
		const [loading, setLoading] = useState(false);
		const [error, setError] = useState<string | null>(null);
		const [success, setSuccess] = useState(false);
		const [message, setMessage] = useState<string | null>(null);

		const reviewSubmission = useCallback(
			async (
				submissionId: string,
				payload: ReviewAssessmentSubmissionPayload
			): Promise<AssessmentSubmission | null> => {
				try {
					setLoading(true);
					setError(null);
					setSuccess(false);
					setMessage(null);

					const response =
						await reviewAssessmentSubmissionService.reviewSubmission(
							submissionId,
							payload
						);

					if (!response?.success) {
						const errorMessage =
							response?.message ||
							'Failed to review assessment submission';

						setError(errorMessage);
						return null;
					}

					setData(response.data ?? null);
					setSuccess(true);
					setMessage(
						response.message || 'Submission reviewed successfully'
					);

					return response.data ?? null;
				} catch (error: any) {
					console.error(
						'useReviewAssessmentSubmission error:',
						error
					);

					const errorMessage =
						error?.response?.data?.message ||
						error?.response?.data?.error ||
						error?.message ||
						'Failed to review assessment submission';

					setError(errorMessage);
					setSuccess(false);

					return null;
				} finally {
					setLoading(false);
				}
			},
			[]
		);

		const reset = useCallback(() => {
			setData(null);
			setLoading(false);
			setError(null);
			setSuccess(false);
			setMessage(null);
		}, []);

		return {
			reviewSubmission,
			data,
			loading,
			error,
			success,
			message,
			reset,
		};
	};